import type { UseQueryOptions } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import {
  useUserAuthQuery as useUserAuthQueryBase,
  useUserQuery as useUserQueryBase,
} from 'codegen/generated/user'
import type {
  UserAuthQuery,
  UserAuthQueryVariables,
  UserQueryVariables,
  UserQuery,
} from 'codegen/generated/user'
import ms from 'ms'
import { getUserIdSelector, isLoggedInQuerySelector } from './selectors'

interface UseIsLoggedInQueryOptions {
  enabled?: boolean
}

export function useIsLoggedInQuery(options?: UseIsLoggedInQueryOptions) {
  return useUserAuthQuery(undefined, {
    ...options,
    select: isLoggedInQuerySelector,
  })
}

export function useIsAuthenticated() {
  const { data } = useIsLoggedInQuery()

  return Boolean(data)
}

export function useSetUserQuery() {
  const queryClient = useQueryClient()

  function setUserQuery(user: UserQuery) {
    queryClient.setQueryData<UserQuery>(useUserQueryBase.getKey(), user)
  }

  return { setUserQuery }
}

export function useUserIdQuery() {
  return useUserAuthQuery(undefined, {
    select: getUserIdSelector,
  })
}

export const userAuthQueryOptions = {
  staleTime: ms('20s'),
}

export function useUserAuthQuery<D = UserAuthQuery>(
  variables?: UserAuthQueryVariables,
  options?: {
    select?: (data: UserAuthQuery) => D
  },
) {
  return useUserAuthQueryBase(variables, {
    ...userAuthQueryOptions,
    ...options,
  })
}

export function getUserAuthFetcher(headers?: RequestInit['headers']) {
  return useUserAuthQueryBase.fetcher(undefined, headers)
}

export const userAuthQueryFetcher = useUserAuthQueryBase.fetcher()
export const userAuthQueryKey = useUserAuthQueryBase.getKey()

export const userQueryOptions = {
  staleTime: ms('20s'),
}

type UseUserQueryOptions = Omit<UseQueryOptions<UserQuery>, 'queryKey' | 'queryFn' | 'select'>

export function useUserQuery<D = UserQuery>(
  variables?: UserQueryVariables,
  options?: UseUserQueryOptions & {
    select?: (data: UserQuery) => D
  },
) {
  return useUserQueryBase(variables, {
    ...userQueryOptions,
    ...options,
  })
}

export const userQueryRootKey = useUserQueryBase.rootKey
export const userQueryKey = useUserQueryBase.getKey()
export const userQueryFetcher = useUserQueryBase.fetcher()
